import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import AiGenerativeTextIcon from '@atlaskit/icon-lab/core/ai-generative-text';

import { Ati } from '@atlassian/ari';
import {
	ExperienceName,
	useAIMateExperienceTracker,
} from '@atlassian/conversation-assistant-instrumentation';

import { expValEquals } from '@confluence/feature-experiments';
import type { ExperimentalPromotedSpacesData } from '@confluence/legacy-schema-bridge/entry-points/experimentalPromotedSpacesQuery.ts';
import { getDefaultSpacesForCreate } from '@confluence/space-utils/entry-points/defaultSpacesForCreate.ts';
import { useSmartCreationModalActions } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';
import { UniversalCreateWithRovoSpotlight } from '@confluence/onboarding-ai-first-create/entry-points/UniversalCreateWithRovoSpotlight.ts';
import { HOME } from '@confluence/named-routes/entry-points/routes.ts';
import { fg } from '@confluence/feature-gating';
import { useStableCallback } from '@confluence/use-stable/entry-points/useStableCallback.ts';

import { useCwrSplitButtonExperiment } from '../cwrExperiment/useCwrSplitButtonExperiment.ts';
import { ItemTitleWithLozenge } from '../ItemTitle.tsx';
import { useSmartCreatePreload } from '../useSmartCreatePreload.ts';
import { useUniversalCreateState } from '../useUniversalCreate.ts';
import { useIsOutOfRovoCredits } from '../../useIsOutOfRovoCredits.ts';
import type { GetPromotedSpacesQueryVariables } from '../../queries/__types__/GetPromotedSpacesQuery.ts';

import { AiCreditMenuItemGuard } from './AiCreditMenuItemGuard.tsx';
import type { MenuItemWrapperChildren } from './AiCreditMenuItemGuard.tsx';
import type { UseMenuItemProps, UseMenuItemReturnProps } from './index.tsx';

export type UseAISmartCreateItemProps = UseMenuItemProps;

const i18n = defineMessages({
	smartCreateTitle: {
		id: 'create-blank-fabric-page.create.content.smart-create-with-ai.title',
		defaultMessage: 'Create with Rovo',
		description: 'title indicating that this option will trigger an AI guided creation workflow',
	},
});

type CreateWithRovoItemWrapperProps = {
	children: MenuItemWrapperChildren;
	title: string;
	onSpotlightClick: () => void;
	isLoading: boolean;
	source: string;
};

/** Skips the Rovo spotlight while the out-of-credits popup is anchored to the item. */
const CreateWithRovoItemWrapper = ({
	children,
	title,
	onSpotlightClick,
	isLoading,
	source,
}: CreateWithRovoItemWrapperProps): React.JSX.Element => (
	<AiCreditMenuItemGuard
		title={title}
		renderAllowed={(renderItem) => (
			<UniversalCreateWithRovoSpotlight
				onClick={onSpotlightClick}
				isLoading={isLoading}
				source={source}
			>
				{renderItem()}
			</UniversalCreateWithRovoSpotlight>
		)}
	>
		{children}
	</AiCreditMenuItemGuard>
);

const getSpaceKeyForCreation = async (): Promise<string> => {
	const spaceKeyRequest: GetPromotedSpacesQueryVariables = {
		promotedSpaceKey: undefined,
	};

	const spaceKeyResponse: ExperimentalPromotedSpacesData = (
		await getDefaultSpacesForCreate({
			variables: { ...spaceKeyRequest, promotedSpacesLimit: 1, otherSpacesLimit: 1 },
		})
	).data;

	return spaceKeyResponse?.experimentalPromotedSpaces?.nodes?.[0]?.id ?? '';
};

/**
 * This is a menu item for an internal demo/spike of a new AI powered creation workflow.
 */
export function useAISmartCreateItem({
	spaceKey,
	onClick,
	source,
	isDisabled = false,
}: UseAISmartCreateItemProps): UseMenuItemReturnProps & {
	promptSuggestionsPreloader: React.ReactNode;
} {
	const { formatMessage } = useIntl();
	const { openModal } = useSmartCreationModalActions();
	const { isOpen } = useUniversalCreateState();
	const experienceTracker = useAIMateExperienceTracker();
	const [fetchedSpaceKey, setFetchedSpaceKey] = useState<string>('');
	const [spaceKeyLoading, setSpaceKeyLoading] = useState<boolean>(true);
	const { isEnabled: isCwrSplitButtonEnabled } = useCwrSplitButtonExperiment();

	// Promoted Spaces query should be cached, but we do have a race condition here for global create where there
	// can potentially be missing space key. This is fine for the early spike stage.
	useEffect(() => {
		setSpaceKeyLoading(true);
		void getSpaceKeyForCreation()
			.then(setFetchedSpaceKey)
			.finally(() => setSpaceKeyLoading(false));
	}, []);

	const { preload: smartCreatePreload, promptSuggestionsPreloader } = useSmartCreatePreload({
		isDisabled,
	});
	useEffect(() => {
		if (isCwrSplitButtonEnabled || !isOpen) {
			return;
		}
		smartCreatePreload();
	}, [isCwrSplitButtonEnabled, isOpen, smartCreatePreload]);

	const handleOpenCreateWithRovoExperience = useCallback(() => {
		experienceTracker.start({
			name: ExperienceName.AIFC_LOAD_STAGING_AREA_MODAL,
			id: ExperienceName.AIFC_LOAD_STAGING_AREA_MODAL,
			attributes: {
				product: 'confluence',
				source,
			},
		});

		// Always pass context - the modal will decide whether to use it for display
		// based on experiment and entry point
		openModal({ entryPoint: source, spaceKey: spaceKey ?? fetchedSpaceKey });
	}, [experienceTracker, fetchedSpaceKey, openModal, source, spaceKey]);

	const handleItemOnClick = useCallback(
		(e: any, actionSubjectId: string, attributes?: Record<string, string>) => {
			onClick(actionSubjectId, attributes);

			// Check if this is a modifier key click and if so, let the default
			// link behavior handle opening a new tab to home route with
			// smart create modal open
			if (e?.metaKey || e?.ctrlKey) {
				return;
			}

			// If this is not a modifier key click, prevent default link
			// navigation and instead open up the modal
			e.preventDefault();

			// Open the modal
			handleOpenCreateWithRovoExperience();
		},
		[onClick, handleOpenCreateWithRovoExperience],
	);

	// Stable so the wrapper isn't remounted (closing an open popup) when the space key resolves.
	const handleSpotlightClick = useStableCallback(handleOpenCreateWithRovoExperience);

	const CreateWithRovoSpotlightWrapper = useMemo(() => {
		return (props: { children: MenuItemWrapperChildren }) => (
			<CreateWithRovoItemWrapper
				title={formatMessage(i18n.smartCreateTitle)}
				onSpotlightClick={handleSpotlightClick}
				isLoading={spaceKeyLoading}
				source={source}
			>
				{props.children}
			</CreateWithRovoItemWrapper>
		);
	}, [formatMessage, handleSpotlightClick, source, spaceKeyLoading]);

	// For all `cmd` / `ctrl` + click, we want to open the home route
	// in a new tab with the smart creation modal open
	const href = HOME.toUrl({}, { query: { openCreateWithRovo: 'true' } });
	const isOutOfCredits = useIsOutOfRovoCredits();

	const getChildren = () => {
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		if (fg('aifc_onboarding_ga_enabled')) {
			if (expValEquals('cc-aifc-databases-experiment', 'isEnabled', true)) {
				return formatMessage(i18n.smartCreateTitle);
			}
		}

		return (
			<ItemTitleWithLozenge useLabsLozenge>
				{formatMessage(i18n.smartCreateTitle)}
			</ItemTitleWithLozenge>
		);
	};

	return {
		promptSuggestionsPreloader,
		ati: Ati.parse(`ati:cloud:confluence:rovo`), // todo: a temporary one, we don't know the type identifier yet
		children: getChildren(),
		Wrapper: CreateWithRovoSpotlightWrapper,
		onClick: (e: any) => handleItemOnClick(e, 'createAISmartCreatePopoverItem'),
		// Without an `href` the item renders as a button, so "Open in new tab" can't bypass the popup.
		href: isOutOfCredits ? undefined : href,
		elemBefore: AiGenerativeTextIcon,
		testId: `create-menu-link-item`,
	};
}
